import { useNavigate } from "react-router-dom";
import { IUser } from "../types";

export interface ISession {
  userId: string;
  token: string;
  userERP: IUser;
}

export const getUserFromLocalStorage = (): ISession | null => {
  try {
    const token = localStorage.getItem("token");
    const userERPString = localStorage.getItem("userERP");
    const userBanco = localStorage.getItem("userId");

    if (!token || !userERPString || !userBanco) {
      return null;
    }

    return {
      token: token,
      userId: userBanco,
      userERP: JSON.parse(userERPString),
    };
  } catch (error) {
    console.error("Erro ao recuperar dados do usuário:", error);
    return null;
  }
};

// FUNÇÕES NÃO UTILIZADAS

let showAlertFunction: (message: string, severity: "error" | "success") => void;

export const handleLoginChange = (
  value: string | null | undefined,
  isFone: boolean
): string => {
  // Se o valor for null ou undefined, substitui por uma string vazia
  value = value || "";

  // Remove todos os caracteres não numéricos
  let numericValue = value.replace(/\D/g, "");

  if (isFone) {
    // Formatação para telefone (fixo ou celular)

    // Limita a 11 dígitos (DDD + 9 dígitos)
    numericValue = numericValue.substring(0, 11);

    if (numericValue.length <= 2) {
      // Apenas DDD: (43
      return `(${numericValue}`;
    } else if (numericValue.length <= 6) {
      // DDD + parte inicial: (43) 3333
      return `(${numericValue.substring(0, 2)}) ${numericValue.substring(2)}`;
    } else if (numericValue.length <= 10) {
      // Telefone fixo completo: (43) 3333-3333
      return `(${numericValue.substring(0, 2)}) ${numericValue.substring(2, 6)}-${numericValue.substring(6, 10)}`;
    } else {
      // Celular completo: (43) 99808-4644
      return `(${numericValue.substring(0, 2)}) ${numericValue.substring(2, 7)}-${numericValue.substring(7, 11)}`;
    }
  } else {
    // Formatação para CPF

    // Limita a 11 dígitos
    numericValue = numericValue.substring(0, 11);

    if (numericValue.length <= 3) {
      return numericValue;
    } else if (numericValue.length <= 6) {
      return numericValue.replace(/(\d{3})(\d{1,3})/, "$1.$2");
    } else if (numericValue.length <= 9) {
      return numericValue.replace(/(\d{3})(\d{3})(\d{1,3})/, "$1.$2.$3");
    } else {
      return numericValue.replace(
        /(\d{3})(\d{3})(\d{3})(\d{1,2})/,
        "$1.$2.$3-$4"
      );
    }
  }
};

export const validateCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/\D/g, ""); // Remove não numéricos

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf[10])) return false;

  return true;
};

export const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, "").slice(0, 11); // Remove caracteres não numéricos e limita a 11 dígitos

  if (cleaned.length <= 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  } else {
    return cleaned.replace(/(\d{2})(\d{1})(\d{4})(\d{0,4})/, "($1) $2 $3-$4");
  }
};

export const useLogout = () => {
  const navigate = useNavigate();

  const logout = () => {
    // 1. Limpa os dados de autenticação
    localStorage.removeItem("token"); // Remove o token do localStorage

    // 2. Redireciona para a rota de login
    navigate("/", { replace: true }); // O `replace: true` impede que a página anterior fique no histórico

    // 3. Recarrega a página para garantir que o estado da aplicação seja resetado
    window.location.reload();
  };

  return logout;
};

export const setAlertFunction = (fn: typeof showAlertFunction) => {
  showAlertFunction = fn;
};

export const showSessionExpiredAlert = () => {
  if (showAlertFunction) {
    showAlertFunction("Sessão expirada - faça login novamente", "error");
  }
};

export const compressImage = async (
  blob: Blob,
  maxWidth = 800,
  quality = 0.7
): Promise<Blob> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(maxWidth / img.width, 1);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (result) => {
          URL.revokeObjectURL(url);
          resolve(result || new Blob());
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(blob); // Retorna o original se der erro
    };

    img.src = url;
  });
};

export const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export function getDefaultPetImage(id: number): string {
  // Se o ID for ímpar, retorna petDefault1.png, se for par retorna petDefault2.png
  return id % 2 !== 0
    ? require("../../assets/petDefault1.png")
    : require("../../assets/petDefault2.png");
}

export const getUserInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// ==== AGE HELPERS (anos e meses) ====

export interface IAge {
  years: number;
  months: number;
}

export const calculateAge = (birthDate?: string | Date | null): IAge | null => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  if (months < 0) {
    years--;
    months += 12;
  }
  if (today.getDate() < birth.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }
  if (years < 0) return null; // data futura inválida
  return { years, months };
};

export const formatAge = (ageObj: IAge | null | undefined): string => {
  if (!ageObj) return "Idade não informada";
  const { years, months } = ageObj;
  if (years === 0 && months === 0) return "Recém-nascido";
  if (years === 0) return `${months} ${months === 1 ? "mês" : "meses"}`;
  if (months === 0) return `${years} ${years === 1 ? "ano" : "anos"}`;
  return `${years} ${years === 1 ? "ano" : "anos"} e ${months} ${
    months === 1 ? "mês" : "meses"
  }`;
};

// ==== PET PAYLOAD NORMALIZATION ====

/** Converte valores de data para 'YYYY-MM-DD'. Aceita string ou Date. */
const toDateOnly = (value?: string | Date | null): string | undefined => {
  if (!value) return undefined;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().split("T")[0];
  } catch {
    return undefined;
  }
};

/** Remove chaves com valor undefined ou null */
const stripEmpty = (obj: Record<string, any>) => {
  Object.keys(obj).forEach((k) => {
    if (obj[k] === undefined || obj[k] === null || obj[k] === "") {
      delete obj[k];
    }
  });
  return obj;
};

/**
 * Normaliza o payload de atualização/envio de Pet conforme API (#API_DOCUMENTATION.md):
 *  - Converte datas para YYYY-MM-DD
 *  - Converte peso para number
 *  - Mapeia isDeceased -> deceased
 *  - Remove campos somente leitura (age, tutor object, photo)
 *  - Usa campo 'foto' (prioriza overrides)
 */
export const normalizePetUpdatePayload = (
  pet: any,
  overrides: Record<string, any> = {}
) => {
  const isDeceased = pet?.isDeceased ?? pet?.deceased ?? false;

  const payload: Record<string, any> = {
    name: pet?.name?.trim(),
    gender: pet?.gender || undefined,
    breed: pet?.breed || undefined,
    species: pet?.species || undefined,
    weight:
      pet?.weight !== undefined && pet?.weight !== ""
        ? Number(pet.weight)
        : undefined,
    photo:
      overrides.foto !== undefined
        ? overrides.foto
        : pet?.foto || pet?.photo || undefined,
    deceased: isDeceased,
    deathDate:
      isDeceased && pet?.deathDate ? toDateOnly(pet.deathDate) : undefined,
    dateOfBirth: toDateOnly(pet?.dateOfBirth),
    dateOfPurchase: toDateOnly(pet?.dateOfPurchase),
    color: pet?.color || undefined,
    // Qualquer campo extra permitido pode ser adicionado aqui
  };

  // Remove campos indesejados da origem
  // (age, tutor object, photo redundante, isDeceased).
  return stripEmpty(payload);
};

/**
 * Formata um número de telefone brasileiro
 * Aceita 10 ou 11 dígitos e formata como (XX) XXXX-XXXX ou (XX) X XXXX-XXXX
 */
export const formatPhone = (value: string): string => {
  if (!value) return "";

  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");

  // Limita a 11 dígitos
  const limited = numbers.substring(0, 11);

  // Aplica a formatação
  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 6) {
    return `(${limited.substring(0, 2)}) ${limited.substring(2)}`;
  } else if (limited.length <= 10) {
    // Telefone fixo: (XX) XXXX-XXXX
    return `(${limited.substring(0, 2)}) ${limited.substring(2, 6)}-${limited.substring(6)}`;
  } else {
    // Celular: (XX) X XXXX-XXXX
    return `(${limited.substring(0, 2)}) ${limited.substring(2, 3)} ${limited.substring(3, 7)}-${limited.substring(7, 11)}`;
  }
};

/**
 * Formata CPF ou CNPJ dinamicamente
 * CPF: XXX.XXX.XXX-XX (11 dígitos)
 * CNPJ: XX.XXX.XXX/XXXX-XX (14 dígitos)
 */
export const formatCpfCnpj = (value: string): string => {
  if (!value) return "";

  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");

  // Se tem mais de 11 dígitos, formata como CNPJ
  if (numbers.length > 11) {
    // Limita a 14 dígitos
    const limited = numbers.substring(0, 14);

    // Aplica formatação de CNPJ
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 5) {
      return `${limited.substring(0, 2)}.${limited.substring(2)}`;
    } else if (limited.length <= 8) {
      return `${limited.substring(0, 2)}.${limited.substring(2, 5)}.${limited.substring(5)}`;
    } else if (limited.length <= 12) {
      return `${limited.substring(0, 2)}.${limited.substring(2, 5)}.${limited.substring(5, 8)}/${limited.substring(8)}`;
    } else {
      return `${limited.substring(0, 2)}.${limited.substring(2, 5)}.${limited.substring(5, 8)}/${limited.substring(8, 12)}-${limited.substring(12, 14)}`;
    }
  } else {
    // Formata como CPF (limita a 11 dígitos)
    const limited = numbers.substring(0, 11);

    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.substring(0, 3)}.${limited.substring(3)}`;
    } else if (limited.length <= 9) {
      return `${limited.substring(0, 3)}.${limited.substring(3, 6)}.${limited.substring(6)}`;
    } else {
      return `${limited.substring(0, 3)}.${limited.substring(3, 6)}.${limited.substring(6, 9)}-${limited.substring(9, 11)}`;
    }
  }
};

/**
 * Remove a formatação de telefone, mantendo apenas números
 */
export const unformatPhone = (value: string): string => {
  return value.replace(/\D/g, "");
};

/**
 * Remove a formatação de CPF/CNPJ, mantendo apenas números
 */
export const unformatCpfCnpj = (value: string): string => {
  return value.replace(/\D/g, "");
};

/**
 * Formata CEP no formato XXXXX-XXX
 */
export const formatCep = (value: string): string => {
  if (!value) return "";

  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");

  // Limita a 8 dígitos
  const limited = numbers.substring(0, 8);

  // Aplica formatação de CEP
  if (limited.length <= 5) {
    return limited;
  } else {
    return `${limited.substring(0, 5)}-${limited.substring(5, 8)}`;
  }
};
